'use client';

import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { z } from 'zod';
import { deserializeAuthSession, type SerializedAuthSession } from '@/lib/auth/session-serialization';
import { authClient } from '@/lib/auth-client';
import type { AuthSession } from '@/types/auth';
import { isAuthSession } from '@/types/auth';

interface SessionState {
  isAuthenticated: boolean;
  isLoading: boolean;
  session: AuthSession | null;
}

const SessionContext = createContext<SessionState>({
  session: null,
  isAuthenticated: false,
  isLoading: false,
});

interface SessionBoundaryProperties {
  children: React.ReactNode;
  initialSession?: AuthSession | SerializedAuthSession | null | undefined;
}

const authResultSchema = z
  .object({
    data: z.unknown().optional(),
    isPending: z.boolean().optional(),
  })
  .passthrough();

const parseAuthResult = (value: unknown): { data: AuthSession | null; isPending: boolean } => {
  const parsed = authResultSchema.safeParse(value);
  if (!parsed.success) {
    return { data: null, isPending: false };
  }
  const candidate = parsed.data.data;
  const session = candidate && isAuthSession(candidate) ? candidate : null;
  return {
    data: session,
    isPending: parsed.data.isPending ?? false,
  };
};

/**
 * Bridges the server-known session into client renders so we avoid flickering the
 * "logged out" UI while Better Auth boots its client store. We still subscribe to
 * Better Auth's client hook so live updates flow through once it resolves.
 */
export function SessionBoundary({ children, initialSession = null }: SessionBoundaryProperties) {
  const rawResult: unknown = authClient.useSession();
  const sessionResult = parseAuthResult(rawResult);
  const clientSession = sessionResult.data;
  const decodedInitialSession = deserializeAuthSession(initialSession);

  const mergedSession = clientSession ?? decodedInitialSession ?? null;
  const mergedIsAuthenticated = Boolean(mergedSession?.user);
  const mergedIsLoading = sessionResult.isPending && !mergedSession;

  const value = useMemo<SessionState>(
    () => ({
      session: mergedSession,
      isAuthenticated: mergedIsAuthenticated,
      isLoading: mergedIsLoading,
    }),
    [mergedIsAuthenticated, mergedIsLoading, mergedSession]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionState(): SessionState {
  return useContext(SessionContext);
}
